import { ApplicationConfig, ErrorHandler, isDevMode } from "@angular/core";
import { provideRouter, Router } from "@angular/router";
import { provideAnimations } from "@angular/platform-browser/animations";
import { APP_BASE_HREF } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withInterceptors,
} from "@angular/common/http";

import { provideAngularSvgIcon } from "angular-svg-icon";
import { OVERLAY_DEFAULT_CONFIG } from "@angular/cdk/overlay";

import { provideIonicAngular } from "@ionic/angular/standalone";
import { Capacitor } from "@capacitor/core";

import { provideTransloco } from "@jsverse/transloco";
import { TranslocoHttpLoader } from "./transloco-http-loader";

import { provideQuangConfig } from "quang";
import { provideAuth } from "quang/auth";
import { quangLoaderInterceptor, withLoaderExcludedUrls } from "quang/loader";

import { BASE_PATH } from "./apiModule";
import { ErrorInterceptor } from "./core/error.interceptor";
import { APP_ROUTES } from "./app.routes";
import { environment } from "../environments/environment";
import { httpNativeInterceptor } from "./core/http.interceptor.fn";
import * as Sentry from "@sentry/angular";

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(APP_ROUTES),
    provideIonicAngular({
      useSetInputAPI: true  //allow to use signal input in modals
    }),
    provideAngularSvgIcon(),
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: BASE_PATH, useValue: `${environment.domain}/oniverse-productfeedback` },
    {
      provide: APP_BASE_HREF,
      useValue: environment.baseUrl || "/",
    },
    provideHttpClient(
      withInterceptors([httpNativeInterceptor, quangLoaderInterceptor]),
      withInterceptorsFromDi(),
    ),
    provideTransloco({
      config: {
        availableLangs: ["en", "it"],
        defaultLang: "en",
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideQuangConfig(
      { baseHref: "/", verbose: !environment.production },
      withLoaderExcludedUrls([
        { url: "openid-connect/token", method: "POST" },
        { url: "assets", method: "GET" },
        { url: "/rest/v1/product/search", method: "POST" },
        { url: "/rest/v1/product/", method: "GET" },
        { url: "/rest/v1/attributes/search/", method: "GET" },
        { url: "/image/", method: "GET" },
        { url: "/countUnread", method: "POST" },
        { url: "/countUnreadMessages", method: "POST" },
        { url: "/countArchivedUnreadMessages", method: "POST" },
        { url: "/chat", method: "GET" },
      ]),
    ),
    provideAuth({
      issuer: `${environment.keycloakUrl}/realms/${environment.keycloakConfig.realm}`,
      clientId: environment.keycloakConfig.clientId,
      redirectUri: Capacitor.isNativePlatform()
        ? environment.redirectUriApp
        : window.location.origin + window.location.pathname,
      responseType: "code",
      scope: "openid profile email roles",
      disableAtHashCheck: true,
      showDebugInformation: !environment.production,
      autoLogin: true,
      sendAccessToken: true,
      urlsToSendToken: [environment.domain],
      useSilentRefresh: false,
      revokeTokensOnLogout: true,
      getUserProfileOnLoginSuccess: true,
    }),
    ...(environment.production
      ? [
          {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
          },
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
        ]
      : []),
  ],
};
